import React from "react";
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  createSession,
  updateSession,
  deleteSession,
  fetchSessionRequestsForCourse,
  cancelSessionRequest,
  fetchSessionContent,
  downloadContentFile,
  fetchClassLevelContent,
  addClassLevelContent,
  fetchSessionInstructorNotes,
  updateSessionInstructorNotes,
} from "../../api/backend";
import SessionRequestDialog from "../../components/SessionRequestDialog";
import {
  parseSessionTopics,
  materialsForSession,
} from "../studentSessionHelpers";
import Icon from "./sessionsList/Icon";
import LessonPlanStrip from "./sessionsList/LessonPlanStrip";
import Notice from "./sessionsList/Notice";
import PendingRequests from "./sessionsList/PendingRequests";
import SessionDetails from "./sessionsList/SessionDetails";
import SessionRow from "./sessionsList/SessionRow";
import SessionFormDialog from "./sessionsList/SessionFormDialog";
import CheckinQrDialog from "./sessionsList/CheckinQrDialog";
import OffHoursConfirmDialog from "./sessionsList/OffHoursConfirmDialog";
import SessionsHeader from "./sessionsList/SessionsHeader";
import {
  countByStatus,
  groupSessions,
  sessionDisplayNumber,
  sessionTitle,
} from "./sessionsList/sessionsModel";
import "./sessionsList/sessionsList.css";

const NOTICE_MS = 5000;

const emptyForm = {
  name: "",
  date: "",
  startTime: "08:00",
  endTime: "12:00",
  sessionType: "PHYSICAL",
  location: "",
  trackLocation: true,
  meetingLink: "",
  enabled: false,
  topics: "",
};

export default function SessionsTab({
  sessions,
  setSessions,
  refreshCourse,
  canEdit,
  canToggleEnabled,
  canEditAttendance,
  course,
  isInstructor,
  currentUserId,
  checklists = [],
  tests = [],
}) {
  const { id: courseId } = useParams();
  const navigate = useNavigate();

  const [qrSession, setQrSession] = React.useState(null);
  const [qrOffHours, setQrOffHours] = React.useState(false);
  const [offHoursPrompt, setOffHoursPrompt] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editTarget, setEditTarget] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [deleteConfirm, setDeleteConfirm] = React.useState(null);

  const [filter, setFilter] = React.useState("all");

  const [sessionRequests, setSessionRequests] = React.useState([]);
  const [requestDialogOpen, setRequestDialogOpen] = React.useState(false);
  const [cancelingId, setCancelingId] = React.useState(null);

  const [expandedId, setExpandedId] = React.useState(null);
  const [sessionDocs, setSessionDocs] = React.useState({});
  const [docsLoadingId, setDocsLoadingId] = React.useState(null);

  const [lessonPlanDocs, setLessonPlanDocs] = React.useState([]);
  const [lessonPlanLoading, setLessonPlanLoading] = React.useState(false);
  const [lessonPlanUploading, setLessonPlanUploading] = React.useState(false);

  const canSeeInstructorNotes = canEdit || isInstructor;
  const [instructorNotes, setInstructorNotes] = React.useState({});
  const [notesLoadingId, setNotesLoadingId] = React.useState(null);
  const [notesSavingId, setNotesSavingId] = React.useState(null);

  const canRequestSession = isInstructor && !canEdit;

  const [notice, setNotice] = React.useState(null);
  const noticeTimer = React.useRef(null);
  React.useEffect(() => () => clearTimeout(noticeTimer.current), []);

  async function toggleExpand(sessionId) {
    const next = expandedId === sessionId ? null : sessionId;
    setExpandedId(next);
    if (next == null) return;
    if (!sessionDocs[next] && !sessionDocs[String(next)]) {
      setDocsLoadingId(next);
      try {
        const items = await fetchSessionContent(next);
        setSessionDocs((prev) => ({ ...prev, [next]: Array.isArray(items) ? items : [] }));
      } catch (e) {
        console.error("Failed to load session content", e);
        setSessionDocs((prev) => ({ ...prev, [next]: [] }));
      } finally {
        setDocsLoadingId(null);
      }
    }
    if (canSeeInstructorNotes && instructorNotes[next] === undefined) {
      setNotesLoadingId(next);
      try {
        const notes = await fetchSessionInstructorNotes(next);
        setInstructorNotes((prev) => ({ ...prev, [next]: notes || "" }));
      } catch (e) {
        console.error("Failed to load instructor notes", e);
        setInstructorNotes((prev) => ({ ...prev, [next]: "" }));
      } finally {
        setNotesLoadingId(null);
      }
    }
  }

  async function handleSaveInstructorNotes(sessionId, notes) {
    setNotesSavingId(sessionId);
    try {
      const saved = await updateSessionInstructorNotes(sessionId, notes);
      setInstructorNotes((prev) => ({ ...prev, [sessionId]: saved || "" }));
    } catch (e) {
      console.error("Failed to save instructor notes", e);
    } finally {
      setNotesSavingId(null);
    }
  }

  React.useEffect(() => {
    if (!courseId) return;
    setLessonPlanLoading(true);
    fetchClassLevelContent(courseId)
      .then((items) => setLessonPlanDocs(Array.isArray(items) ? items : []))
      .catch((e) => {
        console.error("Failed to load course lesson plan", e);
        setLessonPlanDocs([]);
      })
      .finally(() => setLessonPlanLoading(false));
  }, [courseId]);

  async function handleUploadLessonPlanDoc(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLessonPlanUploading(true);
    try {
      const created = await addClassLevelContent(courseId, {
        title: file.name,
        contentType: "DOCUMENT",
        file,
      });
      setLessonPlanDocs((prev) => [...prev, created]);
    } catch (err) {
      console.error("Failed to upload lesson plan document", err);
    } finally {
      setLessonPlanUploading(false);
    }
  }

  async function openLessonPlanDoc(item) {
    try {
      const blobUrl = await downloadContentFile(item.id);
      window.open(blobUrl, "_blank");
    } catch (e) {
      console.error("Failed to open lesson plan document", e);
    }
  }

  React.useEffect(() => {
    if (!courseId) return;
    fetchSessionRequestsForCourse(courseId)
      .then((reqs) => setSessionRequests(Array.isArray(reqs) ? reqs : []))
      .catch(() => setSessionRequests([]));
  }, [courseId]);

  const pendingRequests = sessionRequests.filter((r) => r.status === "pending");

  async function handleCancelRequest(requestId) {
    setCancelingId(requestId);
    try {
      await cancelSessionRequest(requestId, currentUserId);
      setSessionRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (e) {
      console.error("Failed to cancel request", e);
    } finally {
      setCancelingId(null);
    }
  }

  const nextSessionNumber = `S-${String(sessions.length + 1).padStart(3, "0")}`;

  const openAdd = () => {
    if (!canEdit) return;
    setEditTarget(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (session) => {
    if (!canEdit) return;
    setEditTarget(session);
    setForm({
      name: session.name || "",
      date: session.sessionDate || session.date,
      startTime: session.startTime,
      endTime: session.endTime,
      sessionType: session.sessionType || "PHYSICAL",
      location: session.location || "",
      trackLocation: session.trackLocation !== false,
      meetingLink: session.meetingLink || "",
      enabled: session.enabled === true,
      topics: session.topics || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editTarget) {
        const updated = await updateSession(editTarget.id, form);
        setSessions((prev) =>
          prev.map((s) => (s.id === updated.id ? updated : s)),
        );
      } else {
        const created = await createSession(courseId, {
          ...form,
          sessionNumber: nextSessionNumber,
          sessionDate: form.date,
        });
        setSessions((prev) => [...prev, created]);
      }
      setDialogOpen(false);
      if (refreshCourse) refreshCourse();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sessionId) => {
    if (!canEdit) return;
    await deleteSession(sessionId);
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    setDeleteConfirm(null);
    if (refreshCourse) refreshCourse();
  };

  const showNotice = (next) => {
    setNotice(next);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), NOTICE_MS);
  };

  /** Publishes or unpublishes a session. A successful change is announced with an Undo; a failure leaves the session as it was. */
  const handleTogglePublished = async (session, enabled, announce = true) => {
    if (!canToggleEnabled) return;
    const title = sessionTitle(session, sessions);
    try {
      const updated = await updateSession(session.id, { enabled });
      setSessions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      if (!announce) return;
      showNotice({
        kind: "ok",
        message: `${title} ${enabled ? "published. Students can see it and check in." : "unpublished. Hidden from students."}`,
        undo: () => handleTogglePublished(session, !enabled, false),
      });
    } catch (e) {
      console.error("Failed to update session visibility", e);
      showNotice({ kind: "error", message: `Could not update ${title}. Try again.` });
    }
  };

  // ── Derived data ────────────────────────────────────────────────────────────
  const activeStudents = course?.studentCount ?? 0;
  const counts = countByStatus(sessions);
  const groups = groupSessions(sessions, filter);
  // Whoever takes attendance may show the check-in code; the server limits instructors to their own sessions.
  const permissions = { canEdit, canToggleEnabled, canEditAttendance, canShowCheckinQr: canEditAttendance };
  const openAttendance = (session) =>
    navigate(`/course-management/${courseId}/attendance/${session.id}`, { state: { session, course } });

  function renderDetails(session, status) {
    const materials = materialsForSession({
      sessionId: session.id,
      sessionDocs,
      checklists,
      tests,
      status: status === "live" ? "today" : status,
      forceUnlock: true,
    });
    return (
      <SessionDetails
        session={session}
        topics={parseSessionTopics(session.topics)}
        materials={materials}
        docsBusy={docsLoadingId === session.id}
        notesLoading={notesLoadingId === session.id}
        notes={instructorNotes[session.id] ?? ""}
        notesSaving={notesSavingId === session.id}
        canEdit={canEdit}
        canSeeNotes={canSeeInstructorNotes}
        onSaveNotes={handleSaveInstructorNotes}
      />
    );
  }

  const listNote = canRequestSession
    ? "You can request new sessions. Your course coordinator will review and approve them."
    : canEdit
      ? "You're a Course Coordinator — create, edit, publish, and delete sessions."
      : "Only Course Coordinators can create, edit, or delete course sessions.";

  return (
    <Box className="sl-root">
      <SessionsHeader
        counts={counts}
        filter={filter}
        onFilter={setFilter}
        canCreate={canEdit}
        canRequest={canRequestSession}
        onCreate={openAdd}
        onRequest={() => setRequestDialogOpen(true)}
      />

      <LessonPlanStrip
        docs={lessonPlanDocs}
        loading={lessonPlanLoading}
        uploading={lessonPlanUploading}
        canUpload={canEdit}
        onUpload={handleUploadLessonPlanDoc}
        onOpen={openLessonPlanDoc}
      />
      <p className="note"><Icon n="info" s={15} />{listNote}</p>

      {sessions.length === 0 && (
        <div className="empty">No sessions yet.{canEdit ? ' Click "New session" to get started.' : ""}</div>
      )}
      {sessions.length > 0 && groups.length === 0 && <div className="empty">No sessions match the selected filter.</div>}

      {groups.map((group) => (
        <section key={group.key}>
          <div className="grp">
            <h3>
              {group.key === "live"
                ? <><span className="pulse" aria-hidden="true" />Live now</>
                : <><span className="eo"><Icon n="eyeOff" /></span>Not published</>}
            </h3>
            <span>
              {group.items.length} session{group.items.length === 1 ? "" : "s"}
              {group.key === "hidden" ? " hidden from students" : ""}
            </span>
          </div>
          <div className="list">
            {group.items.map(({ session, status }) => (
              <SessionRow
                key={session.id}
                session={session}
                status={status}
                number={sessionDisplayNumber(session, sessions)}
                title={sessionTitle(session, sessions)}
                totalStudents={session.totalStudents ?? activeStudents}
                permissions={permissions}
                expanded={expandedId === session.id}
                onExpand={() => toggleExpand(session.id)}
                onTogglePublished={(next) => handleTogglePublished(session, next)}
                onTakeAttendance={() => openAttendance(session)}
                onViewAttendance={() => openAttendance(session)}
                onShowQr={({ offHours, state }) => {
                  if (offHours) {
                    setOffHoursPrompt({ session, state });
                  } else {
                    setQrOffHours(false);
                    setQrSession(session);
                  }
                }}
                onEdit={() => openEdit(session)}
                onDelete={() => setDeleteConfirm(session)}
              >
                {expandedId === session.id && renderDetails(session, status)}
              </SessionRow>
            ))}
          </div>
        </section>
      ))}

      <PendingRequests
        requests={pendingRequests}
        cancelingId={cancelingId}
        canCancel={(req) => canEdit || String(req.instructorId) === String(currentUserId)}
        onCancel={handleCancelRequest}
      />

      <OffHoursConfirmDialog
        open={!!offHoursPrompt}
        session={offHoursPrompt?.session}
        state={offHoursPrompt?.state}
        onCancel={() => setOffHoursPrompt(null)}
        onConfirm={() => {
          setQrOffHours(true);
          setQrSession(offHoursPrompt.session);
          setOffHoursPrompt(null);
        }}
      />

      <CheckinQrDialog
        open={!!qrSession}
        session={qrSession}
        acknowledgeOffHours={qrOffHours}
        onClose={() => { setQrSession(null); setQrOffHours(false); }}
      />

      <Notice notice={notice} onUndo={() => { const undo = notice?.undo; setNotice(null); if (undo) undo(); }} />

      {/* Add / Edit Dialog */}
      <SessionRequestDialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        courseId={courseId}
        instructorId={currentUserId}
        onSuccess={() => {
          fetchSessionRequestsForCourse(courseId)
            .then((reqs) => setSessionRequests(Array.isArray(reqs) ? reqs : []))
            .catch(() => {});
        }}
      />

      <SessionFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        saving={saving}
        canEdit={canEdit}
        editTarget={editTarget}
        number={editTarget ? editTarget.sessionNumber || "" : nextSessionNumber}
        course={course}
        sessionCount={sessions.length}
        form={form}
        setForm={setForm}
      />

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: "var(--font-display)" }}>Delete Session?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{deleteConfirm?.sessionNumber}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirm.id)}
            variant="contained"
            color="error"
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
